import { ArrowDown, ArrowUp, BadgeCheck, CalendarDays, Check, CheckCircle2, ChevronLeft, ChevronRight, Folder, GripVertical, MessageCircle, MoreHorizontal, Pencil, Plus, Sparkles, Trash2, X, Zap } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type FormEvent, type PointerEvent as ReactPointerEvent } from 'react';
import { Link } from './navigation';
import { Avatar, EmptyState, Priority, Status, formatDate } from './components';
import { boardStatusForColumn, reorderTaskItems, type TaskDateKind } from './task-drag';
import type { Task } from './types';
import { TaskIcon } from './TaskIcon';

export const taskStatusOptions = [
  { value: 'todo', label: 'To do' },
  { value: 'doing', label: 'Doing' },
  { value: 'review', label: 'Review' },
  { value: 'blocked', label: 'Blocked' },
  { value: 'done', label: 'Done' },
];

export type TaskView = 'spreadsheet' | 'board' | 'calendar' | 'timeline';

type TaskDragState = {
  itemKey: string;
  dragging: boolean;
  deltaX: number;
  deltaY: number;
  dropKey: string;
  placeAfter: boolean;
};

const idleTaskDrag: TaskDragState = {
  itemKey: '', dragging: false, deltaX: 0, deltaY: 0, dropKey: '', placeAfter: false,
};

function useTaskPointerDrag({ targetData, onDrop }: {
  targetData: 'taskDropStatus' | 'taskDropDate' | 'taskDropId';
  onDrop?: (itemKey: string, dropKey: string, placeAfter: boolean) => void;
}) {
  const [state, setState] = useState<TaskDragState>(idleTaskDrag);
  const gesture = useRef({ pointerId: -1, itemKey: '', startX: 0, startY: 0, dragging: false, dropKey: '', placeAfter: false });
  const selector = `[data-${targetData.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)}]`;

  const start = useCallback((itemKey: string, event: ReactPointerEvent<HTMLButtonElement>) => {
    if (!onDrop || (event.pointerType === 'mouse' && event.button !== 0)) return;
    event.preventDefault();
    try { event.currentTarget.setPointerCapture(event.pointerId); } catch { /* The window listeners below remain the fallback. */ }
    gesture.current = { pointerId: event.pointerId, itemKey, startX: event.clientX, startY: event.clientY, dragging: false, dropKey: '', placeAfter: false };
    setState({ ...idleTaskDrag, itemKey });
  }, [onDrop]);

  const move = useCallback((event: PointerEvent) => {
    const current = gesture.current;
    if (!onDrop || current.pointerId !== event.pointerId) return;
    const deltaX = event.clientX - current.startX;
    const deltaY = event.clientY - current.startY;
    const dragging = current.dragging || Math.hypot(deltaX, deltaY) > 5;
    if (!dragging) return;
    event.preventDefault();
    const target = document.elementFromPoint(event.clientX, event.clientY)?.closest(selector) as HTMLElement | null;
    const dropKey = target?.dataset[targetData] || '';
    const bounds = target?.getBoundingClientRect();
    const placeAfter = Boolean(bounds && targetData === 'taskDropId' && event.clientY > bounds.top + bounds.height / 2);
    gesture.current = { ...current, dragging, dropKey, placeAfter };
    setState({ itemKey: current.itemKey, dragging, deltaX, deltaY, dropKey, placeAfter });
  }, [onDrop, selector, targetData]);

  const finish = useCallback((event: PointerEvent, cancelled = false) => {
    const current = gesture.current;
    if (current.pointerId !== event.pointerId) return;
    if (!cancelled && current.dragging && current.dropKey) onDrop?.(current.itemKey, current.dropKey, current.placeAfter);
    gesture.current = { pointerId: -1, itemKey: '', startX: 0, startY: 0, dragging: false, dropKey: '', placeAfter: false };
    setState(idleTaskDrag);
  }, [onDrop]);

  useEffect(() => {
    if (!state.itemKey) return;
    const movePointer = (event: PointerEvent) => move(event);
    const finishPointer = (event: PointerEvent) => finish(event);
    const cancelPointer = (event: PointerEvent) => finish(event, true);
    window.addEventListener('pointermove', movePointer, { passive: false });
    window.addEventListener('pointerup', finishPointer);
    window.addEventListener('pointercancel', cancelPointer);
    return () => {
      window.removeEventListener('pointermove', movePointer);
      window.removeEventListener('pointerup', finishPointer);
      window.removeEventListener('pointercancel', cancelPointer);
    };
  }, [finish, move, state.itemKey]);

  return {
    enabled: Boolean(onDrop),
    state,
    bind: (itemKey: string) => ({
      onPointerDown: (event: ReactPointerEvent<HTMLButtonElement>) => start(itemKey, event),
      onPointerMove: (event: ReactPointerEvent<HTMLButtonElement>) => move(event.nativeEvent),
      onPointerUp: (event: ReactPointerEvent<HTMLButtonElement>) => finish(event.nativeEvent),
      onPointerCancel: (event: ReactPointerEvent<HTMLButtonElement>) => finish(event.nativeEvent, true),
    }),
  };
}

function draggedClass(state: TaskDragState, itemKey: string) {
  return state.dragging && state.itemKey === itemKey ? ' is-task-dragging' : '';
}

function draggedStyle(state: TaskDragState, itemKey: string): CSSProperties | undefined {
  if (!state.dragging || state.itemKey !== itemKey) return undefined;
  return { '--task-drag-x': `${state.deltaX}px`, '--task-drag-y': `${state.deltaY}px` } as CSSProperties;
}

function TaskDragHandle({ itemKey, label, drag }: {
  itemKey: string;
  label: string;
  drag: ReturnType<typeof useTaskPointerDrag>;
}) {
  if (!drag.enabled) return null;
  return <button type="button" className="task-drag-handle" aria-label={label} title={label} {...drag.bind(itemKey)}><GripVertical /></button>;
}

export function TaskViewSwitcher({ value, onChange }: { value: TaskView; onChange: (view: TaskView) => void }) {
  const views: Array<{ value: TaskView; label: string }> = [
    { value: 'spreadsheet', label: 'Spreadsheet' },
    { value: 'board', label: 'Board' },
    { value: 'calendar', label: 'Calendar' },
    { value: 'timeline', label: 'Timeline' },
  ];
  return <div className="nv-view-tabs" role="tablist" aria-label="Task workspace view">
    {views.map((view) => <button type="button" role="tab" aria-selected={value === view.value}
      className={`nv-view-tab ${value === view.value ? 'is-active' : ''}`} key={view.value}
      onClick={() => onChange(view.value)}>{view.label}</button>)}
  </div>;
}

export function QuickTaskComposer({ onCreate, open, onOpenChange, placeholder = 'What needs to be done?' }: {
  onCreate: (title: string) => Promise<void>;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  placeholder?: string;
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const isOpen = open ?? internalOpen;
  const setOpen = (value: boolean) => {
    if (open === undefined) setInternalOpen(value);
    onOpenChange?.(value);
    if (!value) { setTitle(''); setError(''); }
  };
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const cleanTitle = title.trim();
    if (!cleanTitle || saving) return;
    setSaving(true); setError('');
    try { await onCreate(cleanTitle); setTitle(''); }
    catch (requestError) { setError((requestError as Error).message); }
    finally { setSaving(false); }
  }
  if (!isOpen) return <button type="button" className="quick-task-add" onClick={() => setOpen(true)}><Plus /> Add task</button>;
  return <form className="quick-task-composer" onSubmit={submit}>
    {title.trim() ? <TaskIcon title={title} /> : <span><Plus /></span>}<input autoFocus value={title} onChange={(event) => setTitle(event.target.value)} placeholder={placeholder} aria-label="Task title" />
    <button type="submit" className="quick-task-composer__save" disabled={!title.trim() || saving} aria-label="Add task"><Check /></button>
    <button type="button" className="quick-task-composer__cancel" onClick={() => setOpen(false)} aria-label="Close quick task entry"><X /></button>
    {error && <small role="alert">{error}</small>}
  </form>;
}

function dateOnly(value: Date) {
  return new Date(Date.UTC(value.getFullYear(), value.getMonth(), value.getDate())).toISOString().slice(0, 10);
}

function mondayOf(value: Date) {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() - ((date.getDay() + 6) % 7));
  return date;
}

function addDays(value: Date, days: number) {
  const date = new Date(value);
  date.setDate(date.getDate() + days);
  return date;
}

export function TaskCalendar({ tasks, onMoveDate }: {
  tasks: Task[];
  onMoveDate?: (task: Task, kind: TaskDateKind, date: string) => void;
}) {
  const [weekOffset, setWeekOffset] = useState(0);
  const start = useMemo(() => addDays(mondayOf(new Date()), weekOffset * 7), [weekOffset]);
  const days = useMemo(() => Array.from({ length: 7 }, (_, index) => addDays(start, index)), [start]);
  const today = dateOnly(new Date());
  const drag = useTaskPointerDrag({
    targetData: 'taskDropDate',
    onDrop: onMoveDate ? (itemKey, date) => {
      const [id, kind] = itemKey.split(':');
      const task = tasks.find((item) => item.id === Number(id));
      if (task && task[(kind === 'due' ? 'due_date' : 'planned_date')] !== date) onMoveDate(task, kind as TaskDateKind, date);
    } : undefined,
  });

  return <div className="task-calendar">
    <div className="task-calendar__toolbar">
      <div><strong>{start.toLocaleDateString('en-IN', { day: 'numeric', month: 'long' })} – {days[6].toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</strong><small>{onMoveDate ? 'Hold the grip and drag a plan or due card to another day' : 'Planned work and due dates from the live task sheet'}</small></div>
      <div><button type="button" onClick={() => setWeekOffset((value) => value - 1)} aria-label="Previous week"><ChevronLeft size={17} /></button><button type="button" onClick={() => setWeekOffset(0)}>Today</button><button type="button" onClick={() => setWeekOffset((value) => value + 1)} aria-label="Next week"><ChevronRight size={17} /></button></div>
    </div>
    <div className="task-calendar__week">
      {days.map((day) => {
        const key = dateOnly(day);
        const events = tasks.flatMap((task) => {
          const matches: Array<{ task: Task; kind: TaskDateKind }> = [];
          if (task.planned_date === key && task.due_date === key) matches.push({ task, kind: 'both' });
          else {
            if (task.planned_date === key) matches.push({ task, kind: 'planned' });
            if (task.due_date === key) matches.push({ task, kind: 'due' });
          }
          return matches;
        });
        const isDropTarget = drag.state.dragging && drag.state.dropKey === key;
        return <section className={`${key === today ? 'is-today' : ''}${isDropTarget ? ' is-task-drop-target' : ''}`} data-task-drop-date={key} key={key}>
          <header><span>{day.toLocaleDateString('en-IN', { weekday: 'short' })}</span><strong>{day.getDate()}</strong></header>
          <div>{events.slice(0, 4).map(({ task, kind }) => {
            const itemKey = `${task.id}:${kind}`;
            return <article className={`task-calendar__event task-calendar__event--${task.status}${draggedClass(drag.state, itemKey)}`} style={draggedStyle(drag.state, itemKey)} key={itemKey}>
              <TaskDragHandle itemKey={itemKey} label={`Move ${task.title} to another day`} drag={drag} />
              <Link className="task-calendar__event-link" to={`/tasks/${task.id}`}>
                <small>{kind === 'both' ? 'Plan & due' : kind === 'due' ? 'Due' : 'Plan'} · {task.project_code || 'General'}</small><strong className="task-icon-title"><TaskIcon title={task.title} /><span>{task.title}</span></strong>
              </Link>
            </article>;
          })}{!events.length && <span className="task-calendar__empty">{isDropTarget ? 'Release to schedule here' : 'No scheduled work'}</span>}{events.length > 4 && <Link className="task-calendar__more" to="/tasks">+{events.length - 4} more</Link>}</div>
        </section>;
      })}
    </div>
  </div>;
}

export function TaskSpreadsheet({ tasks, onStatus, onReorder, onEdit, onDelete, canDelete, compact = false, emptyTitle = 'No tasks found' }: {
  tasks: Task[];
  onStatus?: (task: Task, status: string) => void;
  onReorder?: (tasks: Task[]) => void;
  onEdit?: (task: Task) => void;
  onDelete?: (task: Task) => void;
  canDelete?: (task: Task) => boolean;
  compact?: boolean;
  emptyTitle?: string;
}) {
  type SortKey = 'title' | 'project' | 'assignee' | 'status' | 'priority' | 'planned' | 'due';
  const [sort, setSort] = useState<{ key: SortKey; direction: 'asc' | 'desc' } | null>(null);
  const displayedTasks = useMemo(() => {
    if (!sort) return tasks;
    const priorityRank: Record<string, number> = { low: 1, medium: 2, high: 3, critical: 4 };
    const statusRank: Record<string, number> = { todo: 1, doing: 2, review: 3, blocked: 4, done: 5 };
    const value = (task: Task) => {
      if (sort.key === 'title') return task.title || '';
      if (sort.key === 'project') return `${task.project_code || ''} ${task.project_title || ''}`;
      if (sort.key === 'assignee') return task.assignee_name || '';
      if (sort.key === 'status') return statusRank[task.status] || 99;
      if (sort.key === 'priority') return priorityRank[task.priority] || 99;
      if (sort.key === 'planned') return task.planned_date || '9999-12-31';
      return task.due_date || '9999-12-31';
    };
    return [...tasks].sort((left, right) => {
      const leftValue = value(left); const rightValue = value(right);
      const result = typeof leftValue === 'number' && typeof rightValue === 'number'
        ? leftValue - rightValue : String(leftValue).localeCompare(String(rightValue), undefined, { numeric: true, sensitivity: 'base' });
      return (sort.direction === 'asc' ? result : -result) || left.id - right.id;
    });
  }, [sort, tasks]);
  const changeSort = (key: SortKey) => setSort((current) => ({ key, direction: current?.key === key && current.direction === 'asc' ? 'desc' : 'asc' }));
  const SortHeading = ({ sortKey, children }: { sortKey: SortKey; children: string }) => <button type="button" className={sort?.key === sortKey ? 'task-sort-button is-active' : 'task-sort-button'} onClick={() => changeSort(sortKey)} aria-label={`Sort by ${children}`}>
    <span>{children}</span>{sort?.key === sortKey ? sort.direction === 'asc' ? <ArrowUp /> : <ArrowDown /> : <ArrowDown className="task-sort-button__hint" />}
  </button>;
  const drag = useTaskPointerDrag({
    targetData: 'taskDropId',
    onDrop: onReorder && !sort ? (itemKey, dropKey, placeAfter) => {
      const reordered = reorderTaskItems(tasks, Number(itemKey), dropKey === 'end' ? null : Number(dropKey), placeAfter);
      if (reordered.some((task, index) => task.id !== tasks[index]?.id)) onReorder(reordered);
    } : undefined,
  });
  if (!tasks.length) return <EmptyState title={emptyTitle} description="Tasks matching this workspace will appear in the sheet." icon={CheckCircle2} />;
  const today = dateOnly(new Date());
  const hasActions = Boolean(onEdit || onDelete);
  return <><div className={`task-sheet ${compact ? 'task-sheet--compact' : ''}${hasActions ? ' task-sheet--actions' : ''}`} role="table" aria-label="Task management sheet">
    <div className="task-sheet__row task-sheet__head" role="row">
      <span role="columnheader">{onReorder && !sort ? <GripVertical aria-label="Drag order" /> : '#'} </span><span role="columnheader"><SortHeading sortKey="title">Task</SortHeading></span><span role="columnheader"><SortHeading sortKey="project">Project</SortHeading></span><span role="columnheader"><SortHeading sortKey="assignee">Assignee</SortHeading></span><span role="columnheader"><SortHeading sortKey="status">Status</SortHeading></span><span role="columnheader"><SortHeading sortKey="priority">Priority</SortHeading></span><span role="columnheader"><SortHeading sortKey="planned">Planned</SortHeading></span><span role="columnheader"><SortHeading sortKey="due">Due</SortHeading></span>{hasActions && <span role="columnheader">Actions</span>}
    </div>
    {displayedTasks.map((task, index) => {
      const itemKey = String(task.id);
      const isDropTarget = drag.state.dragging && drag.state.dropKey === itemKey;
      return <div className={`task-sheet__row${draggedClass(drag.state, itemKey)}${isDropTarget ? drag.state.placeAfter ? ' is-task-drop-after' : ' is-task-drop-before' : ''}`} style={draggedStyle(drag.state, itemKey)} data-task-drop-id={itemKey} role="row" key={task.id}>
      <span className="task-sheet__number" role="cell"><TaskDragHandle itemKey={itemKey} label={`Reorder ${task.title}`} drag={drag} /><em>{index + 1}</em></span>
      <span className="task-sheet__task" role="cell"><Link className="task-icon-link" to={`/tasks/${task.id}`}><TaskIcon title={task.title} /><span className="task-icon-copy"><strong>{task.title}</strong><small>#{task.id} · {task.comment_count || 0} comments</small></span></Link></span>
      <span className="task-sheet__project" role="cell"><strong>{task.project_code || 'GENERAL'}</strong><small>{task.project_title || 'General work'}</small></span>
      <span role="cell">{task.assignee_name || 'Unassigned'}</span>
      <span role="cell">{onStatus ? <select aria-label={`Status for ${task.title}`} value={task.status} className={`sheet-status sheet-status--${task.status}`} onChange={(event) => onStatus(task, event.target.value)}>{taskStatusOptions.map((option) => <option value={option.value} key={option.value}>{option.label}</option>)}</select> : <Status value={task.status} />}</span>
      <span role="cell"><Priority value={task.priority} /></span>
      <span role="cell">{formatDate(task.planned_date)}</span>
      <span className={task.due_date && task.due_date < today && task.status !== 'done' ? 'date-overdue' : ''} role="cell">{formatDate(task.due_date)}</span>
      {hasActions && <span className="task-sheet__actions" role="cell">{onEdit && <button type="button" aria-label={`Modify ${task.title}`} title="Modify task" onClick={() => onEdit(task)}><Pencil /></button>}{onDelete && (!canDelete || canDelete(task)) && <button type="button" className="is-danger" aria-label={`Delete ${task.title}`} title="Delete task" onClick={() => onDelete(task)}><Trash2 /></button>}</span>}
    </div>})}
  </div><div className="task-mobile-list" aria-label="Task management list">
    {displayedTasks.map((task, index) => {
      const itemKey = String(task.id);
      const isDropTarget = drag.state.dragging && drag.state.dropKey === itemKey;
      return <article className={`task-mobile-card${draggedClass(drag.state, itemKey)}${isDropTarget ? drag.state.placeAfter ? ' is-task-drop-after' : ' is-task-drop-before' : ''}`} style={draggedStyle(drag.state, itemKey)} data-task-drop-id={itemKey} key={task.id}>
      <header><span>NSV-{task.id}</span><div><Priority value={task.priority} /><TaskDragHandle itemKey={itemKey} label={`Reorder ${task.title}`} drag={drag} /></div></header>
      <Link className="task-mobile-card__title task-icon-link" to={`/tasks/${task.id}`}><TaskIcon title={task.title} /><span className="task-icon-copy"><strong>{task.title}</strong><small>{task.project_code || 'GENERAL'} · {task.project_title || 'General work'}</small></span></Link>
      <div className="task-mobile-card__meta">
        <span><Avatar name={task.assignee_name || 'Unassigned'} color={task.assignee_color} size="sm" /><span><small>Assignee</small><strong>{task.assignee_name || 'Unassigned'}</strong></span></span>
        <span><CalendarDays /><span><small>Due</small><strong className={task.due_date && task.due_date < today && task.status !== 'done' ? 'date-overdue' : ''}>{formatDate(task.due_date)}</strong></span></span>
      </div>
      <footer>{onStatus ? <select aria-label={`Status for ${task.title}`} value={task.status} className={`sheet-status sheet-status--${task.status}`} onChange={(event) => onStatus(task, event.target.value)}>{taskStatusOptions.map((option) => <option value={option.value} key={option.value}>{option.label}</option>)}</select> : <Status value={task.status} />}<span><MessageCircle />{task.comment_count || 0}</span>{hasActions && <div className="task-mobile-card__actions">{onEdit && <button type="button" aria-label={`Modify ${task.title}`} title="Modify task" onClick={() => onEdit(task)}><Pencil /></button>}{onDelete && (!canDelete || canDelete(task)) && <button type="button" className="is-danger" aria-label={`Delete ${task.title}`} title="Delete task" onClick={() => onDelete(task)}><Trash2 /></button>}</div>}<Link to={`/tasks/${task.id}`} aria-label={`Open ${task.title}`}><ChevronRight /></Link></footer>
      <span className="task-mobile-card__index">{index + 1}</span>
    </article>})}
  </div></>;
}

const boardColumns = [
  { key: 'backlog', label: 'Backlog', statuses: ['todo', 'blocked'], icon: Folder },
  { key: 'doing', label: 'In progress', statuses: ['doing'], icon: Zap },
  { key: 'review', label: 'Validation', statuses: ['review'], icon: Sparkles },
  { key: 'done', label: 'Done', statuses: ['done'], icon: BadgeCheck },
] as const;

export function TaskBoard({ tasks, onStatus, onEdit, onDelete, canDelete }: {
  tasks: Task[];
  onStatus?: (task: Task, status: string) => void;
  onEdit?: (task: Task) => void;
  onDelete?: (task: Task) => void;
  canDelete?: (task: Task) => boolean;
}) {
  const drag = useTaskPointerDrag({
    targetData: 'taskDropStatus',
    onDrop: onStatus ? (itemKey, columnKey) => {
      const task = tasks.find((item) => item.id === Number(itemKey));
      const currentColumn = boardColumns.find((column) => (column.statuses as readonly string[]).includes(task?.status || ''))?.key;
      if (task && currentColumn !== columnKey) onStatus(task, boardStatusForColumn(columnKey));
    } : undefined,
  });
  return <div className="nv-board" aria-label="Task board">
    {boardColumns.map((column) => {
      const items = tasks.filter((task) => (column.statuses as readonly string[]).includes(task.status));
      const Icon = column.icon;
      const isDropTarget = drag.state.dragging && drag.state.dropKey === column.key;
      return <section className={`nv-board__column nv-board__column--${column.key}${isDropTarget ? ' is-task-drop-target' : ''}`} data-task-drop-status={column.key} key={column.key}>
        <header className="nv-board__header"><Icon /><strong>{column.label}</strong><span>{items.length}</span><button type="button" aria-label={`${column.label} options`}><MoreHorizontal size={15} /></button></header>
        <div className="nv-board__cards">{items.map((task) => {
          const itemKey = String(task.id);
          return <article className={`nv-task-card${draggedClass(drag.state, itemKey)}`} style={draggedStyle(drag.state, itemKey)} key={task.id}>
            <TaskDragHandle itemKey={itemKey} label={`Move ${task.title} to another status`} drag={drag} />
            {(onEdit || (onDelete && (!canDelete || canDelete(task)))) && <div className="nv-task-card__actions">{onEdit && <button type="button" aria-label={`Modify ${task.title}`} title="Modify task" onClick={() => onEdit(task)}><Pencil /></button>}{onDelete && (!canDelete || canDelete(task)) && <button type="button" className="is-danger" aria-label={`Delete ${task.title}`} title="Delete task" onClick={() => onDelete(task)}><Trash2 /></button>}</div>}
            <Link to={`/tasks/${task.id}`} className="nv-task-card__link">
              <div className="nv-task-card__meta"><span>NSV-{task.id}</span><span className={`nv-task-card__priority nv-task-card__priority--${task.priority}`}>{task.priority}</span></div>
              <h3 className="task-icon-title"><TaskIcon title={task.title} /><span>{task.title}</span></h3><span className="nv-task-card__project">↳ {task.project_title || 'General work'}</span>
              <span className="nv-task-card__due"><CalendarDays />Due: <strong>{formatDate(task.due_date)}</strong></span>
              <footer className="nv-task-card__footer"><Avatar name={task.assignee_name || 'Unassigned'} color={task.assignee_color} size="sm" /><span>{task.assignee_name || 'Unassigned'}</span><span className="nv-task-card__comments"><MessageCircle />{task.comment_count || 0}</span></footer>
            </Link>
          </article>;
        })}{!items.length && <div className="nv-board__empty">{isDropTarget ? `Release to move into ${column.label.toLowerCase()}` : `No tasks in ${column.label.toLowerCase()}`}</div>}</div>
      </section>;
    })}
  </div>;
}

export function TaskTimeline({ tasks }: { tasks: Task[] }) {
  const start = useMemo(() => mondayOf(new Date()), []);
  const days = useMemo(() => Array.from({ length: 7 }, (_, index) => addDays(start, index)), [start]);
  const startTime = new Date(dateOnly(start)).getTime();
  const visible = tasks.filter((task) => task.planned_date || task.due_date).slice(0, 12);
  if (!visible.length) return <EmptyState title="No scheduled tasks" description="Add planned and due dates to build the timeline." icon={CalendarDays} />;
  return <div className="nv-timeline" aria-label="Weekly task timeline">
    <div className="nv-timeline__head"><span>Task</span>{days.map((day) => <span key={dateOnly(day)}>{day.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric' })}</span>)}</div>
    {visible.map((task) => {
      const plannedTime = new Date(task.planned_date || task.due_date || dateOnly(start)).getTime();
      const dueTime = new Date(task.due_date || task.planned_date || dateOnly(start)).getTime();
      const first = Math.max(0, Math.min(6, Math.round((plannedTime - startTime) / 86400000)));
      const last = Math.max(first, Math.min(6, Math.round((dueTime - startTime) / 86400000)));
      return <div className="nv-timeline__row" key={task.id}>
        <Link className="nv-timeline__task task-icon-link" to={`/tasks/${task.id}`}><TaskIcon title={task.title} /><span className="task-icon-copy"><strong>{task.title}</strong><small>{task.project_code || 'GENERAL'} · {task.assignee_name || 'Unassigned'}</small></span></Link>
        {days.map((day) => <span className="nv-timeline__day" key={dateOnly(day)} />)}
        <Link to={`/tasks/${task.id}`} className={`nv-timeline__bar nv-timeline__bar--${task.status}`}
          style={{ gridColumn: `${first + 2} / span ${last - first + 1}` }}>{task.status === 'review' ? 'Validation' : task.status === 'doing' ? 'In progress' : task.status}</Link>
      </div>;
    })}
  </div>;
}
